import { useCallback, useEffect, useState } from 'react';
import type { FocusEvent, MouseEvent, ReactNode } from 'react';
import { Icone } from '../design/Icone';
import type { NomeDoIcone } from '../design/Icone';

/**
 * A BARRA LATERAL do administrador (redesenho de 26/09/2026, pedido do dono: "SaaS premium",
 * logo REAL, vermelho só como destaque, recolhida tratada como produto próprio).
 *
 * Geometria que não pula ao recolher: o ícone de cada linha fica SEMPRE no mesmo x (centro em
 * 36px = metade do trilho de 72), a logo e o monograma trocam por opacidade no mesmo lugar, e o
 * título de grupo vira um fio na MESMA altura — então nada sobe ou desce na animação.
 *
 * A dica do trilho é um elemento `position: fixed` (não um ::after): a lista rola, e o que é
 * desenhado dentro de área com rolagem sai cortado na borda.
 */

export const LOGO = '/branding/inovapay/logo-full.png';
export const MARCA = '/branding/inovapay/logo-mark.png';

type Dica = { readonly texto: string; readonly y: number } | null;

export function useDicaDoTrilho(ativa: boolean) {
  const [dica, setDica] = useState<Dica>(null);
  const mostrar = useCallback(
    (texto: string) => (e: MouseEvent<HTMLElement> | FocusEvent<HTMLElement>) => {
      if (!ativa) return;
      const r = e.currentTarget.getBoundingClientRect();
      setDica({ texto, y: r.top + r.height / 2 });
    },
    [ativa],
  );
  const esconder = useCallback(() => setDica(null), []);
  useEffect(() => {
    if (!ativa) setDica(null);
  }, [ativa]);
  return { dica, mostrar, esconder };
}

export function DicaDoTrilho({ dica }: { dica: Dica }) {
  if (!dica) return null;
  return (
    <div className="sb-dica" role="tooltip" style={{ top: dica.y }}>
      {dica.texto}
    </div>
  );
}

export function TopoDaLateral({
  recolhida,
  podeRecolher,
  aoAlternar,
  aoFechar,
  aoDica,
  aoSairDica,
}: {
  recolhida: boolean;
  /** Notebook (1024–1279) é sempre trilho: não há o que expandir. */
  podeRecolher: boolean;
  aoAlternar: () => void;
  aoFechar: () => void;
  aoDica: (texto: string) => (e: MouseEvent<HTMLElement> | FocusEvent<HTMLElement>) => void;
  aoSairDica: () => void;
}) {
  return (
    <div className="sb-topo">
      <a className="sb-marca" href="#/visao-geral" aria-label="InovaPay — início">
        <img className="sb-logo" src={LOGO} alt="" width={120} height={23} />
        <img className="sb-monograma" src={MARCA} alt="" width={32} height={32} />
      </a>
      {podeRecolher && (
        <button
          type="button"
          className="sb-alternar"
          onClick={aoAlternar}
          aria-label={recolhida ? 'Expandir menu' : 'Recolher menu'}
          aria-pressed={recolhida}
          onMouseEnter={aoDica(recolhida ? 'Expandir menu' : 'Recolher menu')}
          onMouseLeave={aoSairDica}
          onFocus={aoDica(recolhida ? 'Expandir menu' : 'Recolher menu')}
          onBlur={aoSairDica}
        >
          <Icone nome="panel" tamanho={16} traco={1.6} />
        </button>
      )}
      <button type="button" className="sb-fechar" onClick={aoFechar} aria-label="Fechar menu">
        <Icone nome="x" tamanho={18} traco={1.6} />
      </button>
    </div>
  );
}

/** Onde a pessoa está: a unidade e a área do sistema — no lugar da faixa que repetia "InovaPay". */
export function UnidadeAtual({
  area,
  aoDica,
  aoSairDica,
}: {
  area: string;
  aoDica: (texto: string) => (e: MouseEvent<HTMLElement> | FocusEvent<HTMLElement>) => void;
  aoSairDica: () => void;
}) {
  return (
    <div
      className="sb-unidade"
      tabIndex={-1}
      onMouseEnter={aoDica(`Rede Inova · Muriaé — ${area}`)}
      onMouseLeave={aoSairDica}
    >
      <span className="sb-unidade-icone" aria-hidden="true">
        <Icone nome="pin" tamanho={15} traco={1.7} />
      </span>
      <span className="sb-unidade-texto">
        <span className="sb-unidade-nome">Rede Inova · Muriaé</span>
        <span className="sb-unidade-area">{area}</span>
      </span>
    </div>
  );
}

/** Cabeçalho de zona: separa o que é só dos donos (dados da loja) da administração do InovaPay.
 * Só aparece para quem vê as duas — o administrador comum nem sabe que a outra existe. */
export function ZonaDaLateral({ zona }: { zona: 'donos' | 'inovapay' }) {
  const donos = zona === 'donos';
  return (
    <div className={`sb-zona${donos ? ' sb-zona-donos' : ''}`}>
      <span className="sb-zona-icone" aria-hidden="true">
        <Icone nome={donos ? 'lock' : 'cycle'} tamanho={13} traco={1.8} />
      </span>
      <span className="sb-zona-texto">
        <span className="sb-zona-nome">{donos ? 'Área dos donos' : 'Gestão do InovaPay'}</span>
        <span className="sb-zona-nota">{donos ? 'só vocês veem' : 'admins'}</span>
      </span>
    </div>
  );
}

export function GrupoDaLateral({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <div className="sb-grupo" role="group" aria-label={titulo}>
      <div className="sb-grupo-titulo" aria-hidden="true">
        <span>{titulo}</span>
      </div>
      {children}
    </div>
  );
}

export function ItemDaLateral({
  rota,
  rotulo,
  icone,
  ativo,
  contador,
  aoDica,
  aoSairDica,
}: {
  rota: string;
  rotulo: string;
  icone: NomeDoIcone;
  ativo: boolean;
  contador: number | null;
  aoDica: (texto: string) => (e: MouseEvent<HTMLElement> | FocusEvent<HTMLElement>) => void;
  aoSairDica: () => void;
}) {
  const dica = contador ? `${rotulo} · ${String(contador)} aguardando` : rotulo;
  return (
    <a
      href={`#/${rota}`}
      className={`sb-item${ativo ? ' sb-item-ativo' : ''}`}
      aria-current={ativo ? 'page' : undefined}
      onMouseEnter={aoDica(dica)}
      onMouseLeave={aoSairDica}
      onFocus={aoDica(dica)}
      onBlur={aoSairDica}
    >
      <span className="sb-item-icone" aria-hidden="true">
        <Icone nome={icone} tamanho={18} traco={1.6} />
      </span>
      <span className="sb-item-rotulo">{rotulo}</span>
      {contador !== null && contador > 0 && (
        <span className="sb-item-contador" aria-label={`${String(contador)} aguardando`}>
          {contador}
        </span>
      )}
    </a>
  );
}

/**
 * "Nicolas (Desenvolvedor)" vira nome + cargo: o parêntese é o jeito que o cadastro guarda a
 * função. Sem parêntese, o nome inteiro e nenhum cargo.
 */
export function separarCargo(nomeCompleto: string): { nome: string; cargo: string | null } {
  const m = /^(.+?)\s*\(([^()]+)\)\s*$/.exec(nomeCompleto.trim());
  return m?.[1] && m[2]
    ? { nome: m[1].trim(), cargo: m[2].trim() }
    : { nome: nomeCompleto.trim(), cargo: null };
}

export function ContaNaLateral({
  nome,
  papel,
  avatar,
  aberta,
  aoAlternar,
  aoDica,
  aoSairDica,
}: {
  nome: string | null;
  papel: string | null;
  avatar: ReactNode;
  aberta: boolean;
  aoAlternar: () => void;
  aoDica: (texto: string) => (e: MouseEvent<HTMLElement> | FocusEvent<HTMLElement>) => void;
  aoSairDica: () => void;
}) {
  const { nome: primeiro, cargo } = separarCargo(nome ?? 'Sua conta');
  const resumo = [primeiro, cargo, papel].filter(Boolean).join(' · ');
  return (
    <div className="sb-rodape">
      <button
        type="button"
        className="sb-conta"
        onClick={aoAlternar}
        aria-expanded={aberta}
        aria-haspopup="menu"
        aria-label={`Conta: ${resumo}`}
        onMouseEnter={aoDica(resumo)}
        onMouseLeave={aoSairDica}
        onFocus={aoDica(resumo)}
        onBlur={aoSairDica}
      >
        <span className="sb-avatar" aria-hidden="true">
          {avatar}
        </span>
        <span className="sb-conta-texto">
          <span className="sb-conta-nome">{primeiro}</span>
          <span className="sb-conta-papel">
            {[cargo, papel ?? 'Sessão ativa'].filter(Boolean).join(' · ')}
          </span>
        </span>
        <span className="sb-conta-seta" aria-hidden="true">
          <Icone nome="chev-d" tamanho={15} traco={1.7} />
        </span>
      </button>
    </div>
  );
}
